import React, { Props, useEffect, useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { ControlledTextarea } from '../../components/Form/Controlled/ControlledTextarea'
import { Address, OrderInfo, OrderStateHistory } from '../../components'
import { useBasicEntities } from '../../hooks/useBasicEntities'
import { AddressUpdate, OrderUpdate } from './lib/types'
import { UpdateAddressMutationMutationVariables, UpdateOrderMutationMutationVariables } from '../../gql/graphql'
import { CarrierCurrencyPayment } from './components/CarrierCurrencyPayment'
import { TagMultiSelect } from '../../components/TagMultiSelect'
import { OrderDetailsInfoHeader } from './components/OrderDetailsInfoHeader'
import { useGQLMutation } from '../../hooks/GQLUtilityHooks'
import { useQueryClient } from '@tanstack/react-query'
import { dirtyValues } from './lib/utils'
import { FragmentType, graphql, useFragment } from '../../gql'
import { toastSuccess, toastPromise } from '../../lib/toastHelpers'
import { useUnload } from '../../lib/Util'
import { useOrderDetailContext } from './OrderDetailContext'
import { FragmentValue } from '../../lib/RequestUtils'
import { createPortal } from 'react-dom'
import { Button } from '../../components/shadcn/Button'
import { faSave } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useGQLFetcher } from '../../context/GQLClientProvider'

const OrderDetails_AddressFragment = graphql(`
  fragment OrderDetails_AddressFragment on Address {
    id
    firstName
    lastName
    phone
    email
    companyName
    ic
    dic
    street
    zip
    note
    city
    country
    state
    branchId
  }
`)

export const OrderDetailsInfo_OrderFragment = graphql(`
  fragment OrderDetailsInfo_OrderFragment on Order {
    id
    shippingAddress {
      id
      ...OrderDetails_AddressFragment
    }
    carrier {
      id
      name
    }
    currency {
      id
      name
    }
    note
    privateNote
    tags {
      id
      name
      color
    }
    paymentItems {
      id
      amount
      payment {
        id
        name
        type {
          id
          name
        }
      }
      isPaid
    }
    currency {
      id
    }
    packages {
      id
      sent
      code
      carrier {
        id
        name
      }
      tariff {
        id
        name
      }
      weight
      dimensionX
      dimensionY
      dimensionZ
    }
  }
`)

const resetPackageMutation = graphql(`
  mutation ResetPackageFromOrderInfo($id: Int!) {
    resetPackage(id: $id) {
      errors
    }
  }
`)

export const OrderDetailsInfo: React.FC = () => {
  const context = useOrderDetailContext()
  const order = useFragment(OrderDetailsInfo_OrderFragment, context.order)

  const { tags: allTags } = useBasicEntities()
  const queryClient = useQueryClient()
  const history = useHistory()
  const resetPackageQuery = useGQLFetcher(resetPackageMutation)

  // Form setup
  const orderForm = useForm<
    Omit<UpdateOrderMutationMutationVariables, 'paymentItems'> & {
      paymentItems: { amount: string; isPaid: boolean; paymentItemId: number; payment: string }[]
    }
  >()
  const addressForm = useForm<UpdateAddressMutationMutationVariables>({
    defaultValues: {
      country: 'CZ',
    },
  })

  // Mutations
  const { mutateAsync: updateOrder, isPending: isUpdatingOrder } = useGQLMutation(
    updateOrderMutation,
    ['updateOrder', order.id],
    {
      onSuccess: data => {
        toastSuccess('Objednávka byla úspěšně uložena')
        queryClient.setQueryData(['order', { id: order.id }], (old: any) => {
          return {
            order: {
              ...old.order,
              data: {
                ...old.order.data,
                ...data,
              },
            },
          }
        })
      },
    },
  )

  const {
    mutateAsync: updateAddress,
    error: updateAddressError,
    isPending: isUpdatingAddress,
  } = useGQLMutation(updateAddressMutation, ['updateAddress', order.id], {
    onSuccess: shippingAddress => {
      toastSuccess('Adresa byla úspěšně uložena')
      queryClient.setQueryData(['order', { id: order.id }], (old: any) => {
        if (shippingAddress) {
          addressForm.reset(addressToAddressUpdate(shippingAddress))
        }
        return {
          order: {
            ...old.order,
            data: {
              ...old.order.data,
              shippingAddress: shippingAddress,
            },
          },
        }
      })
    },
  })

  const {
    mutateAsync: createAddress,
    error: createAddressError,
    isPending: isCreatingAddress,
  } = useGQLMutation(createAddressMutation, ['createAddress', order.id], {
    onSuccess: data => {
      toastSuccess('Adresa byla úspěšně vytvořena')
      addressForm.reset(addressToAddressUpdate(data))
      queryClient.setQueryData(['order', { id: order.id }], (old: any) => {
        return {
          order: {
            ...old.order,
            data: { ...old.order.data, shippingAddress: data },
          },
        }
      })
    },
  })

  const isSaving = isUpdatingOrder || isUpdatingAddress || isCreatingAddress
  const isDirty = orderForm.formState.isDirty || addressForm.formState.isDirty
  console.log('isDirty', isDirty, orderForm.formState.dirtyFields, addressForm.formState.dirtyFields)

  // Save handlers
  const handleSaveOrder = orderForm.handleSubmit(async data => {
    let dirty = { ...orderForm.formState.dirtyFields }

    // Normalize the dirty fields object to ensure it contains only boolean values
    const normalizedDirty = Object.keys(dirty).reduce(
      (acc, key) => {
        acc[key] = !!dirty[key] // Convert to boolean
        return acc
      },
      {} as Record<string, boolean>,
    )

    console.log('normalizedDirty', normalizedDirty)

    if (!!normalizedDirty?.carrier) {
      const isAnyPackageSent = order.packages.some(p => !!p.sent)
      if (isAnyPackageSent) {
        const packagesSentAmount = order.packages.filter(p => !!p.sent).length
        const confirmed = window.confirm(
          `Některé z balíků již byly odeslány (${packagesSentAmount} z ${order.packages.length}). Změna dopravce resetuje všechny balíky včetně odeslaných. V případě potvrzení se uloží nový dopravce a balíky se resetují. V případě zrušení se nic neuloží.`,
        )
        if (!confirmed) {
          return
        }
      }
    }

    // We can't submit payment items with ids, but we need id to implement delete payment item mutation, so let's remove it.
    if (Array.isArray(data.paymentItems)) {
      data.paymentItems.forEach(pi => {
        if ('paymentItemId' in pi) {
          delete pi.paymentItemId
        }
      })
    }

    return updateOrder({
      id: order.id,
      ...dirtyValues(normalizedDirty, data),
      paymentItems: data.paymentItems.map(pi => {
        return {
          ...pi,
          payment: pi.payment ? parseInt(pi.payment) : 0,
        }
      }),
    }).then(async () => {
      if (!normalizedDirty?.carrier) {
        return
      }
      const packagesToReset = order.packages.filter(p => p.id)
      if (packagesToReset.length > 0) {
        await toastPromise(Promise.all(packagesToReset.map(p => resetPackageQuery({ id: p.id }))), {
          pending: 'Resetování balíku...',
          success: 'Balík byl úspěšně resetován',
          error: 'Chyba při resetování balíku',
        })
      }
      queryClient.invalidateQueries({ queryKey: ['order', { id: order.id }] })
    })
  })

  const handleSaveAddress = addressForm.handleSubmit(async data => {
    // Normalize the dirty fields object to ensure it contains only boolean values
    const normalizedDirty = Object.keys(addressForm.formState.dirtyFields).reduce(
      (acc, key) => {
        acc[key] = !!(addressForm.formState.dirtyFields as any)[key] // Convert to boolean
        return acc
      },
      {} as Record<string, boolean>,
    )

    console.log('normalizedDirty', normalizedDirty)

    if (!!order.shippingAddress?.id) {
      return updateAddress({
        ...dirtyValues(normalizedDirty, data),
        country: data.country || 'CZ',
        id: order.shippingAddress.id,
      }).then(() => handleSaveOrder())
    } else {
      return createAddress({ ...dirtyValues(normalizedDirty, data), country: data.country || 'CZ' }).then(data => {
        if (data?.createAddress?.data?.id) {
          return updateOrder({ id: order.id, shippingAddress: data.createAddress.data.id }).then(() =>
            handleSaveOrder(),
          )
        }
      })
    }
  })

  const handleSaveDirty = () => {
    if (addressForm.formState.isDirty) {
      handleSaveAddress().catch(e => console.error(e))
    } else if (orderForm.formState.isDirty) {
      handleSaveOrder().catch(e => console.error(e))
    }
  }

  // Reset forms when order is loaded
  useEffect(() => {
    if (order) {
      orderForm.reset(orderToOrderUpdate(order), { keepDirtyValues: false })
      if (order.shippingAddress) {
        console.log('resetting address form', addressToAddressUpdate(order.shippingAddress))
        addressForm.reset(addressToAddressUpdate(order.shippingAddress), { keepDirtyValues: false })
      } else {
        addressForm.reset({ country: 'CZ' }, { keepDirtyValues: false })
      }
    }
  }, [order])

  // Leave prevention when forms are dirty
  useUnload((e: BeforeUnloadEvent) => {
    if (isDirty) {
      e.preventDefault()
      e.returnValue = 'Máte neuložené změny. Opravdu chcete opustit stránku?'
      return 'Máte neuložené změny. Opravdu chcete opustit stránku?'
    }
  })

  // Block React Router navigation
  useEffect(() => {
    const unblock = history.block(() => {
      console.log('isDirty', isDirty, orderForm.formState.dirtyFields, addressForm.formState.dirtyFields)
      if (isDirty) {
        return 'Máte neuložené změny. Opravdu chcete opustit stránku?'
      }
      return undefined // Allow navigation
    })

    return unblock // Cleanup function to remove the block
  }, [isDirty, history])

  const addressError = (updateAddressError || createAddressError)?.toString()

  return (
    <FormProvider {...orderForm}>
      <div className="flex flex-col items-center h-full overflow-y-auto">
        {/*<OrderDetailsInfoHeader onSave={handleSaveDirty} isDirty={isDirty} isSaving={isSaving} />*/}
        {createPortal(
          <Button
            onClick={handleSaveDirty}
            disabled={!isDirty || isSaving}
            variant="default"
            size="default"
            className="min-w-48">
            <FontAwesomeIcon icon={faSave} className="mr-2" />
            {isSaving ? 'Ukládám...' : 'Uložit změny'}
          </Button>,
          document.getElementById('footer-buttons-portal') || document.body,
        )}
        <div className={'p-main flex flex-col gap-8 flex-1  max-w-screen-xl'}>
          <div className={'grid grid-cols-10 gap-8'}>
            {/* Left Column */}

            <div className={'space-y-5 col-span-6'}>
              <h1 className={'text-black text-2xl font-bold'}>Úprava objednávky</h1>
              <CarrierCurrencyPayment />
              {/* Address Section */}
              <h1 className={'text-black text-2xl font-bold'}>Adresa</h1>
              <Address form={addressForm} error={addressError} />
              {/* Tags Section */}
              <h1 className={'text-black text-2xl font-bold'}>Tagy</h1>
              <div className={'box flex flex-col gap-3'}>
                <Controller
                  control={orderForm.control}
                  name={'tags'}
                  render={({ field: { onChange, value } }) => (
                    <TagMultiSelect
                      allTags={allTags as any}
                      selectedTagIds={Array.isArray(value) ? value : value ? [value] : []}
                      onChange={ids => onChange(ids)}
                      placeholder="Vyberte tagy"
                    />
                  )}
                />
              </div>
              {/* Notes Section */}
              <h1 className={'text-black text-2xl font-bold'}>Poznamky</h1>
              <div className={'box space-y-4'}>
                <ControlledTextarea label={'Poznámka k objednávce'} control={orderForm.control} name={'note'} />
                <ControlledTextarea
                  label={'Interní poznámka k objednávce'}
                  control={orderForm.control}
                  name={'privateNote'}
                />
              </div>
            </div>

            {/* Right Column */}
            <div className={'space-y-5 col-span-4'}>
              <OrderInfo />
              <OrderStateHistory />
            </div>
          </div>
        </div>
      </div>
    </FormProvider>
  )
}

// Helper functions and mutations
const orderToOrderUpdate = (
  order: FragmentValue<typeof OrderDetailsInfo_OrderFragment>,
): Omit<UpdateOrderMutationMutationVariables, 'paymentItems'> & {
  paymentItems: { amount: string; isPaid: boolean; paymentItemId: number; payment: string }[]
} => {
  return {
    id: order.id,
    carrier: order?.carrier?.id || null,
    shippingAddress: order?.shippingAddress?.id || null,
    note: order?.note || '',
    privateNote: order?.privateNote || '',
    tags: order?.tags?.map(tag => tag.id) || [],
    currency: order?.currency?.id || '',
    paymentItems: order?.paymentItems?.map(pi => {
      return {
        amount: (pi.amount || 0).toString(),
        isPaid: pi.isPaid,
        paymentItemId: pi.id,
        payment: pi.payment?.id.toString() || '',
      }
    }),
  }
}

const addressToAddressUpdate = (
  addressFragment: FragmentType<typeof OrderDetails_AddressFragment>,
): UpdateAddressMutationMutationVariables => {
  if (!addressFragment) return { id: 0, country: 'CZ' }

  const address = useFragment(OrderDetails_AddressFragment, addressFragment)
  return {
    id: address.id,
    firstName: address.firstName || '',
    lastName: address.lastName || '',
    note: address.note || '',
    street: address.street || '',
    city: address.city || '',
    zip: address.zip || '',
    country: address.country || 'CZ',
    phone: address.phone || '',
    email: address.email || '',
    ic: address.ic || '',
    dic: address.dic || '',
    branchId: address.branchId || '',
    state: address.state || '',
    companyName: address.companyName || '',
  }
}

export const updateOrderMutation = graphql(`
  mutation UpdateOrderMutation(
    $id: Int!
    $carrier: String = null
    $shippingAddress: Int = null
    $paymentItems: [updatePaymentItem_] = null
    $note: String = null
    $privateNote: String = null
    $tags: [Int!] = null
    # Null is not handled properly for currency key
    $currency: String
  ) {
    updateOrder(
      id: $id
      carrier: $carrier
      shippingAddress: $shippingAddress
      paymentItems: $paymentItems
      note: $note
      privateNote: $privateNote
      tags: $tags
      currency: $currency
    ) {
      data {
        id
        isPaid
        currency {
          id
        }
        paymentItems {
          id
          isPaid
          amount
          payment {
            id
          }
        }
        shippingAddress {
          ...OrderDetails_AddressFragment
        }
        carrier {
          id
          name
          requiredShippingAddress
          tariffs {
            id
            name
          }
        }
        note
        privateNote
        tags {
          id
          name
          color
        }
      }
      errors
    }
  }
`)

export const updateAddressMutation = graphql(`
  mutation UpdateAddressMutation(
    $id: Int!
    $firstName: String = null
    $lastName: String = null
    $note: String = null
    $street: String = null
    $city: String = null
    $zip: String = null
    $country: String!
    $phone: String = null
    $email: String = null
    $ic: String = null
    $dic: String = null
    $branchId: String = null
    $state: String = null
    $companyName: String = null
  ) {
    updateAddress(
      id: $id
      firstName: $firstName
      lastName: $lastName
      note: $note
      street: $street
      city: $city
      zip: $zip
      country: $country
      phone: $phone
      email: $email
      ic: $ic
      dic: $dic
      branchId: $branchId
      state: $state
      companyName: $companyName
    ) {
      data {
        ...OrderDetails_AddressFragment
      }
      errors
    }
  }
`)

export const createAddressMutation = graphql(`
  mutation CreateAddressMutation(
    $firstName: String = null
    $lastName: String = null
    $note: String = null
    $street: String = null
    $city: String = null
    $zip: String = null
    $country: String!
    $phone: String = null
    $email: String = null
    $ic: String = null
    $dic: String = null
    $branchId: String = null
    $state: String = null
    $companyName: String = null
  ) {
    createAddress(
      firstName: $firstName
      lastName: $lastName
      note: $note
      street: $street
      city: $city
      zip: $zip
      country: $country
      phone: $phone
      email: $email
      ic: $ic
      dic: $dic
      branchId: $branchId
      state: $state
      companyName: $companyName
    ) {
      data {
        id
        ...OrderDetails_AddressFragment
      }
      errors
    }
  }
`)
